import {jobBadDates, jobDate, jobDatesFromEvent} from './jobs'

/* Общий редактор событий.
 *
 * Раньше под каждый тип был свой блок: рождение и смерть в «Сведениях»,
 * работа в «Работе». Так можно было завести ровно то, что предусмотрено,
 * а «жил там-то с такого-то года», «служил», «учился» — уже нет. В самом
 * Gramps редактор один и общий, с полным списком типов; делаем так же.
 *
 * Рождение и смерть сюда НЕ отдаём: у персоны на них заведены отдельные
 * указатели (birth_ref_index, death_ref_index) по позиции в списке, и
 * свободное удаление таких событий молча ломает привязку. Они остаются
 * в «Сведениях», где эта тонкость уже учтена.
 */

/** Типы, которые Gramps заводит для человека. Порядок — по частоте у нас. */
export const EVENT_TYPES: [number, string][] = [
  [12, 'Рождение'],
  [13, 'Смерть'],
  [19, 'Погребение'],
  [42, 'Проживание'],
  [37, 'Деятельность'],
  [26, 'Образование'],
  [33, 'Военная служба'],
  [43, 'Выход на пенсию'],
  [24, 'Кремация'],
  [20, 'Причина смерти'],
  [22, 'Крещение'],
  [11, 'Усыновление'],
  [28, 'Эмиграция'],
  [15, 'Крещение (взрослого)'],
  [23, 'Конфирмация'],
  [41, 'Религия'],
  [31, 'Окончание учёбы'],
  [25, 'Учёная степень'],
  [38, 'Рукоположение'],
  [21, 'Перепись'],
  [30, 'Иммиграция'],
  [34, 'Натурализация'],
  [27, 'Избрание'],
  [35, 'Дворянский титул'],
  [40, 'Имущество'],
  [44, 'Завещание'],
  [39, 'Утверждение завещания'],
  [32, 'Медицинские сведения'],
  [45, 'Мертворождение'],
  [16, 'Бар-мицва'],
  [17, 'Бат-мицва'],
  [18, 'Благословение'],
  [29, 'Первое причастие'],
]

/* Первые двенадцать — то, что реально бывает нужно; в нашей базе из 130
   событий вообще встречаются только пять типов. Остальное открывается
   пунктом «Другой тип…»: длинный список с бар-мицвой и утверждением
   завещания в первом же выпадающем меню только отпугивает. */
export const COMMON_COUNT = 12

export const EV_BIRTH = 12
export const EV_DEATH = 13
export const EV_BURIAL = 19

/** Тип по умолчанию у новой строки. */
export const DEFAULT_TYPE = 42

/** Тип приходит строкой на чтение и объектом на запись — знаем оба вида. */
const BY_NAME: Record<string, number> = {
  Residence: 42, Occupation: 37, Education: 26, Military_Service: 33,
  'Military Service': 33, Retirement: 43, Burial: 19, Cremation: 24,
  Christening: 22, Baptism: 15, Confirmation: 23, Religion: 41,
  Graduation: 31, Degree: 25, Ordination: 38, Adopted: 11, Census: 21,
  Emigration: 28, Immigration: 30, Naturalization: 34, Elected: 27,
  'Nobility Title': 35, Property: 40, Will: 44, Probate: 39,
  'Cause Of Death': 20, 'Medical Information': 32, Stillbirth: 45,
  'Bar Mitzvah': 16, 'Bat Mitzvah': 17, Blessing: 18, 'First Communion': 29,
  Birth: 12, Death: 13,
}

export const typeValue = (t: unknown): number => {
  if (typeof t === 'string') return BY_NAME[t] ?? BY_NAME[t.replace(/_/g, ' ')] ?? 0
  return (t as {value?: number} | undefined)?.value ?? 0
}

export const typeName = (v: number): string =>
  EVENT_TYPES.find(([x]) => x === v)?.[1] || (v === 12 ? 'Рождение' : v === 13 ? 'Смерть' : 'Событие')

/** Подпись по строке из API. Незнакомое отдаём как пришло: лучше
 *  английское слово, чем безликое «Событие». */
export const typeRu = (s: string): string => {
  const v = typeValue(s)
  return v ? typeName(v) : s
}

export interface EventEntry {
  /** handle события; null — ещё не сохранялось */
  handle: string | null
  type: number
  from: string
  to: string
  place: string | null
  /** у работы сюда попадает профессия, у прочего — свободное пояснение */
  desc: string
}

export const emptyEvent = (): EventEntry =>
  ({handle: null, type: DEFAULT_TYPE, from: '', to: '', place: null, desc: ''})

/* Пустая — только что добавленная строка, в которой ничего не тронули: её
 * сохранять незачем. Уже записанное событие пустым не считаем никогда,
 * даже если в нём одна лишь пометка «Смерть» без даты — это и есть
 * «человека нет, а когда — неизвестно», самая частая запись у стариков.
 * Убирают события кнопкой, а не опустошением полей. */
export const eventIsEmpty = (e: EventEntry) =>
  !e.handle && e.type === DEFAULT_TYPE
  && !e.place && !e.from.trim() && !e.to.trim() && !e.desc.trim()

export const eventBadDates = (e: EventEntry): boolean =>
  jobBadDates({handle: e.handle, profession: '', employer: '', from: e.from, to: e.to})

interface RawEvent {
  handle: string
  type?: string | {value?: number}
  description?: string
  place?: string | null
  date?: {modifier?: number; dateval?: number[]}
  [k: string]: unknown
}

export const eventFromRaw = (ev: RawEvent): EventEntry => ({
  handle: ev.handle,
  type: typeValue(ev.type),
  place: ev.place || null,
  desc: ev.description || '',
  ...jobDatesFromEvent(ev),
})

/* Правка и создание — РАЗНЫЕ тела.
 *
 * При правке шлём только то, что редактируем, и накладываем на прочитанный
 * объект. Полное тело затирало бы у события цитаты, заметки и снимки: PUT
 * пересобирает объект из присланного, а пустой citation_list — это команда
 * «цитат нет». У рождения и смерти, которые теперь правятся здесь наравне
 * с остальными, источники как раз бывают. */
export function eventPatch(e: EventEntry) {
  return {
    _class: 'Event',
    type: {_class: 'EventType', value: e.type, string: ''},
    description: e.desc.trim(),
    date: jobDate(e.from, e.to),
    place: e.place,
  }
}

/** Чего у нового события нет, но сервер ждёт. Без handle и gramps_id —
 *  их назначает сам сервер. */
export const EVENT_SKELETON = {
  attribute_list: [],
  private: false,
  tag_list: [],
  citation_list: [],
  note_list: [],
  media_list: [],
  change: 0,
}

/** Короткая запись для просмотра: «Проживание · Владивосток, 1979–2005». */
export function eventLabel(e: EventEntry, place: string): string {
  const years = e.from && e.to ? `${e.from}–${e.to}` : e.from || e.to || ''
  return [e.desc.trim(), place, years].filter(Boolean).join(', ')
}
